/**
 * KHIYARSA (KMD): minimal EN/AR strings for the Markdown import UI, kept
 * local to the khiyarsa layer (same pattern as the cloud module).
 */
import { useAppLangCode } from "../../../app-language/language-state";

const en = {
  importMarkdown: "Import Markdown…",
  dialogTitle: "Import Khiyarsa Markdown",
  placeholder:
    "Paste Khiyarsa Markdown here, or choose a .md file.\n\n---\nkhiyarsa: 1\ntype: flow\n---\n\n## First stage\n- Step one\n- Step two",
  chooseFile: "Choose .md file",
  insert: "Insert diagram",
  preview: "Preview",
  summary: "Structure",
  problems: "Problems",
  nodes: "nodes",
  containers: "containers",
  edges: "relationships",
  decisions: "decisions",
  notes: "notes",
  icons: "icons",
  noPreview: "Preview appears when the document compiles without errors.",
  notKmd:
    'This does not look like Khiyarsa Markdown — files must start with frontmatter containing "khiyarsa: 1". See the spec for details.',
  line: "line",
  exportKmd: "Export as KMD (.md)…",
  exportNoProvenance:
    "Nothing to export: the canvas has no KMD-generated elements.",
  copyDiagnostics: "Copy diagnostics (JSON)",
  downloadNormalized: "Download normalized .md",
  copied: "Copied.",
  // ---- AI Diagram Template dialog -----------------------------------------
  agentTemplate: "AI Diagram Template…",
  atDialogTitle: "AI Diagram Template",
  atIntro:
    "Use any capable AI (ChatGPT, Claude, Fable, Codex, Kimi…) to create Khiyarsa diagrams. Give it this official template — it teaches the AI everything it needs to write a valid diagram file for you. No account needed.",
  atStepsTitle: "How to use it",
  atStep1: "Download or copy the template below.",
  atStep2: "Paste it into your AI agent (ChatGPT, Claude, Fable, Codex, Kimi…).",
  atStep3: "Describe the diagram you want under USER REQUEST at the end.",
  atStep4: "Ask the agent to return only the final .md content.",
  atStep5:
    "Back in Khiyarsa, use “Import Markdown…” in this menu to insert the result.",
  atMetaTitle: "About the template",
  atMetaSpec: "Language",
  atMetaFamilies: "Diagram types",
  atMetaCompat: "Works with",
  atMetaCompatOther: "and other capable AI models",
  atMetaOutput: "AI output",
  atMetaOutputValue: "a single .md file",
  atMetaImport: "Import back",
  atMetaImportValue: "menu → “Import Markdown…” (or drag the .md onto the canvas)",
  atDownload: "Download Template (.md)",
  atCopy: "Copy Template",
  atView: "View Template",
  atHide: "Hide Template",
  atCopyExample: "Copy Example Request",
  atRawLink: "Raw file",
  atLoading: "Loading template…",
  atFetchError:
    "Could not load the template. Check your connection and try again.",
};

const ar: typeof en = {
  importMarkdown: "استيراد ماركداون…",
  dialogTitle: "استيراد خيارسة ماركداون",
  placeholder:
    "الصق نص خيارسة ماركداون هنا، أو اختر ملف ‎.md\n\n---\nkhiyarsa: 1\ntype: flow\n---\n\n## المرحلة الأولى\n- الخطوة الأولى\n- الخطوة الثانية",
  chooseFile: "اختر ملف ‎.md",
  insert: "إدراج المخطط",
  preview: "معاينة",
  summary: "البنية",
  problems: "الملاحظات",
  nodes: "عناصر",
  containers: "حاويات",
  edges: "علاقات",
  decisions: "قرارات",
  notes: "ملاحظات",
  icons: "أيقونات",
  noPreview: "تظهر المعاينة عندما يُترجم المستند دون أخطاء.",
  notKmd:
    "لا يبدو هذا ملف خيارسة ماركداون — يجب أن يبدأ الملف بترويسة تحتوي على ‎khiyarsa: 1. راجع المواصفة للتفاصيل.",
  line: "سطر",
  exportKmd: "تصدير كملف KMD‏ (.md)…",
  exportNoProvenance:
    "لا يوجد ما يُصدَّر: لا توجد عناصر مولّدة من KMD على اللوحة.",
  copyDiagnostics: "نسخ الملاحظات (JSON)",
  downloadNormalized: "تنزيل النسخة الموحّدة ‎.md",
  copied: "تم النسخ.",
  // ---- AI Diagram Template dialog -----------------------------------------
  agentTemplate: "قالب مخططات الذكاء الاصطناعي…",
  atDialogTitle: "قالب مخططات الذكاء الاصطناعي",
  atIntro:
    "استخدم أي ذكاء اصطناعي قادر (ChatGPT، Claude، Fable، Codex، Kimi…) لإنشاء مخططات خيارسة. أعطه هذا القالب الرسمي — فهو يعلّم الذكاء الاصطناعي كل ما يحتاجه لكتابة ملف مخطط صالح لك. لا يلزم حساب.",
  atStepsTitle: "طريقة الاستخدام",
  atStep1: "نزّل القالب أدناه أو انسخه.",
  atStep2: "الصقه في وكيل الذكاء الاصطناعي (ChatGPT، Claude، Fable، Codex، Kimi…).",
  atStep3: "صِف المخطط الذي تريده تحت USER REQUEST في نهاية القالب.",
  atStep4: "اطلب من الوكيل أن يعيد محتوى ملف ‎.md النهائي فقط.",
  atStep5:
    "ثم في خيارسة، استخدم «استيراد ماركداون…» من هذه القائمة لإدراج النتيجة.",
  atMetaTitle: "عن القالب",
  atMetaSpec: "اللغة",
  atMetaFamilies: "أنواع المخططات",
  atMetaCompat: "يعمل مع",
  atMetaCompatOther: "وغيرها من نماذج الذكاء الاصطناعي القادرة",
  atMetaOutput: "مخرجات الذكاء الاصطناعي",
  atMetaOutputValue: "ملف ‎.md واحد",
  atMetaImport: "الاستيراد",
  atMetaImportValue:
    "القائمة ← «استيراد ماركداون…» (أو اسحب ملف ‎.md إلى اللوحة)",
  atDownload: "تنزيل القالب (.md)",
  atCopy: "نسخ القالب",
  atView: "عرض القالب",
  atHide: "إخفاء القالب",
  atCopyExample: "نسخ طلب تجريبي",
  atRawLink: "الملف الخام",
  atLoading: "جارٍ تحميل القالب…",
  atFetchError: "تعذّر تحميل القالب. تحقق من اتصالك وحاول مجددًا.",
};

export const useKmdT = () => {
  const [langCode] = useAppLangCode();
  const dict = langCode.startsWith("ar") ? ar : en;
  return (key: keyof typeof en) => dict[key];
};

/** Diagnostic-rendering locale for the current UI language. */
export const useKmdDiagnosticLocale = (): "en" | "ar" => {
  const [langCode] = useAppLangCode();
  return langCode.startsWith("ar") ? "ar" : "en";
};
